import { Component, OnInit } from '@angular/core';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { RegionService } from 'src/app/services/region.service';
import { ComunaService } from 'src/app/services/comuna.service';
import { UsuarioBusqueda, UsuarioBusquedaService } from '@services/usuario-busqueda.service';
import { InstitucionService } from '@services/institucion.service';
import { Institucion } from '@models/institucion.interface';
import { futureDateValidator, todayValidator, maxTwoYearsAgoValidator } from 'src/app/shared/utils/date-validators';
import { SolicitudService } from '@services/solicitud.service';
import { SolicitudSaludIrrecuperableRequest } from '@models/evaluacion-medica.interface';
import { AlertService } from 'src/app/services/alert.service';
import { SessionService } from '@services/session.service';


@Component({
  selector: 'app-solicitud-salud-irrecuperable',
  templateUrl: './solicitud-salud-irrecuperable.component.html',
  styleUrls: ['./solicitud-salud-irrecuperable.component.scss']
})
export class SolicitudSaludIrrecuperableComponent implements OnInit {
  form!: FormGroup;
  submitted = false;
  showPreviewModal = false;

  // Datos de listas desplegables
  paisesOrigen = ['Chile', 'Argentina', 'Bolivia', 'Colombia', 'Otros'];
  sexos = ['Masculino', 'Femenino', 'Otro'];
  
  regiones: any[] = [];
  comunas: any[] = [];
  institucionComunas: any[] = [];
  funcionarioComunas: any[] = [];
  usuario: UsuarioBusqueda;
  institucion: Institucion;

  constructor(
    private fb: FormBuilder,
    private regionService: RegionService,
    private comunaService: ComunaService,
    private usuarioBusquedaService: UsuarioBusquedaService,
    private institucionesService: InstitucionService,
    private solicitudService: SolicitudService,
    private alertService: AlertService,
    private sessionService: SessionService
  ) {}

  ngOnInit(): void {
    this.loadUserData();
    this.initializeForm();
    this.loadRegiones();
    this.setTodayDate();
  }

  private setTodayDate(): void {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;
    
    this.form.get('fechaTermino')?.setValue(formattedDate);
    
    const twoYearsAgo = new Date(today);
    twoYearsAgo.setFullYear(today.getFullYear() - 2);
    const year2 = twoYearsAgo.getFullYear();
    const month2 = String(twoYearsAgo.getMonth() + 1).padStart(2, '0');
    const day2 = String(twoYearsAgo.getDate()).padStart(2, '0');
    const formattedDate2 = `${year2}-${month2}-${day2}`;
    
    this.form.get('fechaInicio')?.setValue(formattedDate2);
  }

  async loadUserData(){
    try {
     const userRut = this.sessionService.getSesionActual().rut_usuario; //TODO this should be the session data
     const userData = await this.usuarioBusquedaService.buscarPorRut(userRut);
     this.usuario = userData;
     console.log('User data:', userData);

     if (!userData.insRut) {
       this.alertService.error('no tiene permisos para esta seccion');
       return;
     }

     const institutionData = await this.institucionesService.buscarPorRut(userData.insRut);
     if (!institutionData) {
       this.alertService.error('Institución no encontrada');
       return;
     }

     this.institucion = institutionData;
     console.log('Institution data:', institutionData);

     const jefeRut = institutionData.ins_jefe_superior_rut ? `${institutionData.ins_jefe_superior_rut}-${institutionData.ins_jefe_superior_rut_dv || ''}` : '';
     this.form.get('solicitanteRut')?.setValue(jefeRut);
     this.form.get('solicitanteCorreo')?.setValue(institutionData.ins_jefe_superior_correo);

     const jefeNameParts = institutionData.ins_jefe_superior ? institutionData.ins_jefe_superior.split(' ') : [];
     this.form.get('solicitanteNombre')?.setValue(jefeNameParts[0] || '');
     this.form.get('solicitanteApellidoPaterno')?.setValue(jefeNameParts[1] || '');
     this.form.get('solicitanteApellidoMaterno')?.setValue(jefeNameParts.slice(2).join(' ') || '');
    
     this.form.get('solicitanteRut')?.disable();
     this.form.get('solicitanteCorreo')?.disable();
     this.form.get('solicitanteNombre')?.disable();
     this.form.get('solicitanteApellidoPaterno')?.disable();
     this.form.get('solicitanteApellidoMaterno')?.disable();

     // Llenar campos de institución
     this.form.get('institucionRut')?.setValue(`${institutionData.ins_rut}-${institutionData.ins_rutdv}`, { emitEvent: false });
     this.form.get('institucionNombre')?.setValue(institutionData.ins_nombre);
     this.form.get('institucionRegion')?.setValue(institutionData.reg_codigo, { emitEvent: false });
     this.form.get('institucionDireccion')?.setValue(institutionData.ins_direccion);
     this.form.get('jefeNombreCompleto')?.setValue(institutionData.ins_jefe_superior);
     this.form.get('institucionCorreoNotificacion')?.setValue(institutionData.ins_correo_notificacion);
     
     this.form.get('institucionRut')?.disable();
     this.form.get('institucionNombre')?.disable();
     this.form.get('institucionRegion')?.disable();
     this.form.get('institucionComuna')?.disable();
     this.form.get('institucionDireccion')?.disable();
     this.form.get('jefeNombreCompleto')?.disable();
  
     await this.loadComunas(institutionData.reg_codigo, 'institucion');
     setTimeout(() => {
       this.form.get('institucionComuna')?.setValue(institutionData.com_codigo);
     }, 600);

    } catch (error) {
      console.error('Error cargando usuario o institución', error);
    }
  }

  async loadInstitutionData() {
    try {
      const rut = this.form.get('institucionRut')?.value;
      if (!rut) return;

      const institutionData = await this.institucionesService.buscarPorRut(rut);
      if (!institutionData) {
        this.alertService.error('Institución no encontrada');
        return;
      }
      this.institucion = institutionData;
      console.log(institutionData);
      
      this.form.get('institucionNombre')?.setValue(institutionData.ins_nombre);
      // Use emitEvent: false to prevent triggering the valueChanges subscription
      this.form.get('institucionRegion')?.setValue(institutionData.reg_codigo, { emitEvent: false });
      
      this.form.get('institucionDireccion')?.setValue(institutionData.ins_direccion);
      this.form.get('jefeNombreCompleto')?.setValue(institutionData.ins_jefe_superior);
      this.form.get('institucionCorreoNotificacion')?.setValue(institutionData.ins_correo_notificacion);
      
      this.form.get('institucionNombre')?.disable();
      this.form.get('institucionRegion')?.disable();
      this.form.get('institucionComuna')?.disable();
      this.form.get('institucionDireccion')?.disable();
      this.form.get('jefeNombreCompleto')?.disable();
   
      await this.loadComunas(institutionData.reg_codigo, 'institucion');
      
      const foundComuna = this.institucionComunas.find(c => c.codigo === institutionData.com_codigo);
      if (!foundComuna) {
        console.warn(`Comuna ${institutionData.com_codigo} no encontrada en la lista de la región ${institutionData.reg_codigo}`);
      }
      setTimeout(() => {
        this.form.get('institucionComuna')?.setValue(institutionData.com_codigo);
      }, 600);

    } catch (error) {
      console.error('Error cargando institucion', error);
    }
  }

  async loadRegiones() {
    try {
      this.regiones = await this.regionService.obtenerRegiones();
      console.log(this.regiones);
    } catch (error) {
      console.error('Error cargando regiones', error);
    }
  }

  async loadComunas(regionId: number, type: 'institucion' | 'funcionario') {
    try {
      console.log(regionId);
      const comunas = await this.comunaService.obtenerComunas(regionId);
      if (type === 'institucion') {
        this.institucionComunas = comunas;
      } else {
        this.funcionarioComunas = comunas;
      }
    } catch (error) {
       console.error('Error cargando comunas', error);
       if (type === 'institucion') {
        this.institucionComunas = [];
      } else {
        this.funcionarioComunas = [];
      }
    }
  }

  //TODO listener para obtener informacion de licencia medica asociada al funcionario
  private initializeForm(): void {
    this.form = this.fb.group({
      // Datos del solicitante
      solicitanteRut: ['', [Validators.required]],
      solicitanteCorreo: ['', [Validators.required, Validators.email]],
      solicitanteNombre: ['', [Validators.required]],
      solicitanteApellidoPaterno: ['', [Validators.required]],
      solicitanteApellidoMaterno: ['', [Validators.required]],
      solicitanteTelefono: ['', [Validators.required]],

      // Datos de la institución
      institucionNombre: ['', [Validators.required]],
      institucionRut: ['', [Validators.required]],
      institucionDireccion: ['', [Validators.required]],
      institucionRegion: ['', [Validators.required]],
      institucionComuna: ['', [Validators.required]],
      jefeNombreCompleto: ['', [Validators.required]],
      institucionCorreoNotificacion: ['', [Validators.required, Validators.email]],

      // Datos del funcionario a evaluar
      funcionarioNombre: ['', [Validators.required]],
      funcionarioRut: ['', [Validators.required]],
      funcionarioApellidoPaterno: ['', [Validators.required]],
      funcionarioApellidoMaterno: ['', [Validators.required]],
      funcionarioFechaNacimiento: ['', [Validators.required, futureDateValidator]],
      funcionarioEdad: [{ value: '', disabled: true }, [Validators.required]],
      funcionarioPaisOrigen: ['Chile', [Validators.required]],
      funcionarioRegion: ['', [Validators.required]],
      funcionarioComuna: ['', [Validators.required]],
      funcionarioDireccionParticular: ['', [Validators.required]],
      funcionarioCorreoElectronico: ['', [Validators.required, Validators.email]],
      funcionarioCorreoValidacion: ['', [Validators.required, Validators.email]],
      funcionarioSexo: ['', [Validators.required]],
      fechaInicio: [{ value: '', disabled: true }],
      fechaTermino: [{ value: '', disabled: true }],
      confirmacionAutorizacion: [false, [Validators.requiredTrue]]
    }, { validators: this.dateRangeValidator });

    this.form.get('institucionRegion')?.valueChanges.subscribe((regionId: number) => {
      this.loadComunas(regionId, 'institucion');
    });

     this.form.get('institucionRut')?.valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged()
     ).subscribe((rut: any) => {
      this.loadInstitutionData();
    });
    
    this.form.get('funcionarioRegion')?.valueChanges.subscribe((regionId: number) => {
       this.loadComunas(regionId, 'funcionario');
    });
    this.form.get('funcionarioFechaNacimiento')?.valueChanges.subscribe((fecha: string) => {
      if (fecha) {
        const edad = this.calcularEdad(fecha);
        this.form.get('funcionarioEdad')?.setValue(edad);
      }
    });
  }

  private calcularEdad(fechaNacimiento: string): number {
    const hoy = new Date();
    const fecha = new Date(fechaNacimiento);
    let edad = hoy.getFullYear() - fecha.getFullYear();
    const mes = hoy.getMonth() - fecha.getMonth();
    if (mes < 0 || (mes === 0 && hoy.getDate() < fecha.getDate())) {
      edad--;
    }
    return edad;
  }

  dateRangeValidator(group: any): any {
    const start = group.get('fechaInicio')?.value;
    const end = group.get('fechaTermino')?.value;

    if (start && end && new Date(start) > new Date(end)) {
      return { dateRange: true };
    }
    return null;
  }

  get f() {
    return this.form.controls;
  }

  async onContinuar(): Promise<void> {
    this.submitted = true;
    if (this.form.valid) {
      console.log('Formulario válido, mostrando vista previa');
      this.showPreviewModal = true;
    } else {
      console.log('Formulario inválido');
      this.alertService.warning('Por favor, complete todos los campos obligatorios antes de continuar.');
    }
  }

  cerrarVistaPrevia(): void {
    this.showPreviewModal = false;
  }

  async confirmSubmit(): Promise<void> {
    if (!this.form.valid) return;
    
    const solicitanteRutRaw = this.form.get('solicitanteRut')?.value || '';
    const [usu_rut, usu_rutdv] = solicitanteRutRaw.split('-');

    const sexoMap: { [key: string]: string } = {
      'Masculino': 'M',
      'Femenino': 'F',
      'Otro': 'O'
    };

    const solicitud: SolicitudSaludIrrecuperableRequest = {
      usu_rut: this.usuario.rut.toString(),
      usu_rutdv: this.usuario.rutDv.toString(),
      usu_nombres: this.form.get('solicitanteNombre')?.value,
      usu_apellido_paterno: this.form.get('solicitanteApellidoPaterno')?.value,
      usu_apellido_materno: this.form.get('solicitanteApellidoMaterno')?.value,
      usu_mail: this.form.get('solicitanteCorreo')?.value,

      fun_rut: this.form.get('funcionarioRut')?.value,
      fun_nombre: this.form.get('funcionarioNombre')?.value,
      fun_apellido_paterno: this.form.get('funcionarioApellidoPaterno')?.value,
      fun_apellido_materno: this.form.get('funcionarioApellidoMaterno')?.value,
      fun_fecha_nacimiento: this.form.get('funcionarioFechaNacimiento')?.value,
      fun_sexo: sexoMap[this.form.get('funcionarioSexo')?.value] || 'O',
      pai_codigo: this.form.get('funcionarioPaisOrigen')?.value === 'Chile' ? 152 : 152, // Default to Chile per example
      reg_codigo: Number(this.form.get('funcionarioRegion')?.value),
      com_codigo: Number(this.form.get('funcionarioComuna')?.value),
      fun_direccion: this.form.get('funcionarioDireccionParticular')?.value,
      fun_correo_electronico: this.form.get('funcionarioCorreoElectronico')?.value,

      ins_rut: this.form.get('institucionRut')?.value,
      ins_nombre: this.form.get('institucionNombre')?.value,
      tin_codigo: 1,
      ins_direccion: this.form.get('institucionDireccion')?.value,
      ins_reg_codigo: Number(this.form.get('institucionRegion')?.value),
      ins_com_codigo: Number(this.form.get('institucionComuna')?.value),
      ins_correo_notificacion: this.form.get('institucionCorreoNotificacion')?.value,
      ins_jefe_superior: this.form.get('jefeNombreCompleto')?.value,
     // fun_fecha_inicio: this.form.get('fechaInicio')?.value,
      //fun_fecha_termino: this.form.get('fechaTermino')?.value,

      exp_observaciones: 'Expediente generado desde formulario web'
    };
    
    try {
      this.alertService.showLoading('Enviando solicitud...');
      const response: any = await this.solicitudService.crearSolicitud(solicitud);
      console.log(response);

      if (typeof response === 'string' && response.includes('error')) {
        const errorMessage = this.formatErrorMessage(response);
        this.alertService.modalError('Error', errorMessage);
        return;
      }

      if (response && (response.success === false || response.success === 'false' || response.error)) {
        let errorMessage = response.error || response.data?.error || 'Hubo un error al enviar la solicitud.';
        errorMessage = this.formatErrorMessage(errorMessage);
        this.alertService.modalError('Error', errorMessage);
        return;
      }

      const folio = response.data?.exp_id;
      await this.alertService.modalSuccess('Solicitud Enviada', `La solicitud N° ${folio} ha sido creada correctamente.`);
      this.cerrarVistaPrevia();
      this.onReset();
    } catch (error: any) {
      console.error('Error al enviar solicitud', error);
      
      let errorMessage = 'Hubo un error al enviar la solicitud. Por favor, intente nuevamente.';
      if (error?.error?.data?.error) {
        errorMessage = error.error.data.error;
      } else if (error?.data?.error) {
        errorMessage = error.data.error;
      } else if (error?.error?.error) {
        errorMessage = error.error.error;
      } else if (error?.error && typeof error.error === 'string') {
        errorMessage = error.error;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      errorMessage = this.formatErrorMessage(errorMessage);
      this.alertService.modalError('Error', errorMessage);
    }
  }

  onReset(): void {
    this.submitted = false;
    this.form.reset({
      funcionarioPaisOrigen: 'Chile',
      confirmacionAutorizacion: false,
    });
    this.setTodayDate();
  }

  private formatErrorMessage(msg: any): string {
    if (typeof msg !== 'string') {
      return msg ? String(msg) : 'Error desconocido';
    }
    
    // Primero intentamos ver si es un JSON (ej: '{"error": "Validaci\\u00f3n incompleta"}')
    try {
      const parsed = JSON.parse(msg);
      if (parsed && parsed.error) {
        return parsed.error; // Esto decodificará automáticamente \u00f3 a ó
      }
    } catch (e) {
      // No es JSON válido, continuar
    }

    // Si no es JSON, pero tiene caracteres \u00f3 literales en el string, intentamos decodificarlo
    if (msg.includes('\\u')) {
      try {
        return JSON.parse(`"${msg.replace(/"/g, '\\"')}"`);
      } catch (e) {
        // Ignorar
      }
    }

    return msg;
  }
}
